import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { History, TrendingUp, TrendingDown, UserMinus, UserCheck, Search, Filter, Download, ArrowLeftRight, Link2, Unlink } from "lucide-react";
import LinkDevirDialog from "@/components/LinkDevirDialog";

const COMPANY = {
  misas: { label: "Misaş", cls: "company-misas" },
  mis_holding: { label: "Mis Holding", cls: "company-mh" },
  koop: { label: "Mis Koop.", cls: "company-koop" },
  "*": { label: "Genel", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

const ACTIONS = [
  { key: "all", label: "Tümü" },
  { key: "update", label: "Hisse Güncellemesi" },
  { key: "depart", label: "Ortaklıktan Çıkarma" },
  { key: "restore", label: "Geri Alma" },
];

const COMPANY_FILTERS = [
  { key: "all", label: "Tümü" },
  { key: "misas", label: "Misaş" },
  { key: "mis_holding", label: "Mis Holding" },
  { key: "koop", label: "Mis Koop." },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function GlobalHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [linkTarget, setLinkTarget] = useState(null);
  const nav = useNavigate();

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/history`, { params: { company, action, q: q.trim() || undefined, limit: 500 } })
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [company, action]);
  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const unlinkRow = async (hid) => {
    if (!window.confirm("Bu devir eşleşmesini kaldırmak istediğinize emin misiniz?")) return;
    try {
      await axios.post(`${API}/history/${hid}/unlink`, {}, { withCredentials: true });
      fetchData();
    } catch (e) { /* fetchData will still refresh */ }
  };

  const downloadCSV = async () => {
    const url = new URL(`${API}/history/export.csv`);
    if (company !== "all") url.searchParams.set("company", company);
    if (action !== "all") url.searchParams.set("action", action);
    if (q.trim()) url.searchParams.set("q", q.trim());
    const r = await fetch(url.toString(), { credentials: "include" });
    if (!r.ok) return;
    const blob = await r.blob();
    const link = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = link; a.download = `gecmis.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(link), 5000);
  };

  const summary = useMemo(() => {
    let inCount = 0, outCount = 0, devirCount = 0;
    for (const r of rows) {
      if (r.action !== "update") continue;
      if (r.delta > 0) inCount++;
      else if (r.delta < 0) outCount++;
      if (r.devir_pair_id && r.delta > 0) devirCount++;
    }
    return { total: rows.length, inCount, outCount, devirCount };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Denetim</div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Sistem Değişiklik Geçmişi</h1>
        <p className="text-slate-600 mt-1 text-sm">Tüm hisse değişiklikleri, ortaklıktan çıkarmalar ve geri almalar. Otomatik eşleşen devirler <span className="inline-flex items-center gap-1"><ArrowLeftRight size={12}/> ikonu</span> ile işaretlenir.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="crisp-card p-4"><div className="text-[11px] uppercase font-bold text-slate-500">Toplam Kayıt</div><div className="text-3xl font-extrabold font-mono-nums text-slate-900 mt-1">{summary.total}</div></div>
        <div className="crisp-card p-4"><div className="text-[11px] uppercase font-bold text-slate-500">Artışlar</div><div className="text-3xl font-extrabold font-mono-nums text-emerald-600 mt-1">+{summary.inCount}</div></div>
        <div className="crisp-card p-4"><div className="text-[11px] uppercase font-bold text-slate-500">Azalışlar</div><div className="text-3xl font-extrabold font-mono-nums text-red-600 mt-1">-{summary.outCount}</div></div>
        <div className="crisp-card p-4"><div className="text-[11px] uppercase font-bold text-slate-500">Devir Çifti</div><div className="text-3xl font-extrabold font-mono-nums text-slate-900 mt-1">{summary.devirCount}</div></div>
      </div>

      <div className="crisp-card p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="history-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Ortak adı veya değiştiren yönetici ara..."
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={15} className="text-slate-400" />
          {COMPANY_FILTERS.map(f => (
            <button key={f.key} data-testid={`hf-company-${f.key}`} onClick={() => setCompany(f.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${company === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>{f.label}</button>
          ))}
        </div>
      </div>
      <div className="crisp-card p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-2">İşlem:</span>
        {ACTIONS.map(a => (
          <button key={a.key} data-testid={`hf-action-${a.key}`} onClick={() => setAction(a.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${action === a.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>{a.label}</button>
        ))}
        <button data-testid="history-csv"
          onClick={downloadCSV}
          className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white hover:bg-slate-900 hover:text-white transition-colors text-xs font-semibold px-3 py-1.5">
          <Download size={13}/> CSV İndir
        </button>
      </div>

      <div className="crisp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Tarih</th>
                <th className="px-4 py-3">Şirket</th>
                <th className="px-4 py-3">İşlem</th>
                <th className="px-4 py-3">Ortak</th>
                <th className="px-4 py-3 text-right">Eski</th>
                <th className="px-4 py-3 text-right">Yeni</th>
                <th className="px-4 py-3 text-right">Δ</th>
                <th className="px-4 py-3">Devir Karşılığı</th>
                <th className="px-4 py-3">Değiştiren</th>
                <th className="px-4 py-3 text-right">Eşleştirme</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">Kayıt bulunamadı.</td></tr>}
              {!loading && rows.map((h, i) => {
                const co = COMPANY[h.company] || COMPANY["*"];
                const pos = (h.delta || 0) > 0;
                const Icon = h.action === "update" ? (pos ? TrendingUp : TrendingDown) : (h.action === "depart" ? UserMinus : UserCheck);
                return (
                  <tr key={h.id}
                      data-testid={`history-row-${h.id}`}
                      onClick={() => h.member_id && nav(`/ortak/${h.member_id}`)}
                      className="border-b border-slate-100 last:border-0 table-row-hover cursor-pointer stagger-row"
                      style={{ animationDelay: `${Math.min(i,20)*15}ms` }}>
                    <td className="px-4 py-3 text-slate-600 font-mono-nums text-xs whitespace-nowrap">{fmt(h.changed_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${co.cls}`}>{co.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                        <Icon size={13} className={h.action === "update" ? (pos ? "text-emerald-600" : "text-red-600") : "text-slate-500"} />
                        {h.action === "update" ? "Güncelleme" : h.action === "depart" ? "Çıkarma" : "Geri Alma"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{h.member_ad_soyad || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-nums text-slate-500">{h.action === "update" ? h.old_value : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-nums text-slate-900 font-bold">{h.action === "update" ? h.new_value : "—"}</td>
                    <td className={`px-4 py-3 text-right font-mono-nums text-xs font-bold ${pos ? "text-emerald-600" : h.delta < 0 ? "text-red-600" : "text-slate-400"}`}>
                      {h.action === "update" ? (pos ? "+" : "") + h.delta : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {h.paired_member_ad_soyad ? (
                        <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
                          <ArrowLeftRight size={11}/> {h.paired_member_ad_soyad}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <div className="font-semibold text-slate-700">{h.changed_by_name || "—"}</div>
                      <div className="text-[11px] text-slate-400">{h.changed_by}</div>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      {h.action === "update" && h.delta !== 0 && (
                        h.devir_pair_id ? (
                          <button data-testid={`history-unlink-${h.id}`}
                            onClick={() => unlinkRow(h.id)}
                            title="Devir bağını kaldır"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-red-700 border border-slate-200 hover:border-red-200 rounded-md px-2 py-1 bg-white hover:bg-red-50">
                            <Unlink size={11}/> Bağı Kaldır
                          </button>
                        ) : (
                          <button data-testid={`history-link-${h.id}`}
                            onClick={() => setLinkTarget(h.id)}
                            title="Bu değişiklikle eşleşecek bir devir seç"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-md px-2 py-1">
                            <Link2 size={11}/> Eşleştir
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
          Görüntülenen: <span className="font-semibold text-slate-800 font-mono-nums">{rows.length}</span> kayıt (son 500)
        </div>
      </div>

      <LinkDevirDialog
        open={!!linkTarget}
        historyId={linkTarget}
        onClose={() => setLinkTarget(null)}
        onLinked={() => fetchData()}
      />
    </div>
  );
}
